/**
 * Invitee experience (section `invitee`, plan 6.F).
 * Group 1 Booking page · Group 2 Invitee questions & guests · Group 3 Booking confirmation · Group 4 Policies.
 *
 * Fields span three save steps exactly as today: `event_url` (type page), `event_image` /
 * `event_description` / policies (details page), questions / flags / confirmation (advanced page).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FEATURE_IDS } from "../../../../../constants/featureAccess";
import { isAtPlanLimit } from "../../../../../utils/planLimits";
import { FRONTEND_BASE_URL, IMAGE_BASE_URL } from "../../../../../utils/utility";

import Icon from "../../ui/Icon";
import { DowngradeNote, FieldError, Helper, PlanBadge, SettingLine, SettingsGroup, Switch } from "../../ui/primitives";
import { useBuilder } from "../../state/builderContext";
import {
  CONFIRMATION_PAGE,
  D,
  E,
  IMAGE_ACCEPT,
  IMAGE_EXTENSIONS,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_OPTIONS,
  ROUTES,
  STEPS,
  questionTypeLabel,
} from "../../state/constants";
import { optionValueFromLabel } from "../../state/payloads";
import SettingsView from "../shared/SettingsView";
import RichOrPlainText from "../shared/RichOrPlainText";

// ---- helpers --------------------------------------------------------------------------------------------------

const issueFor = (issues, field) => {
  const i = (issues || []).find((x) => x.field === field);
  return i ? i.message : null;
};
/** Inline messages appear once the user has edited the step (or on an existing event); the footer always counts them. */
const touched = (state, step) => state.save.version[step] > 0 || !state.meta.isNew;

const flip = (v) => (v === E ? D : E);
const baseName = (path) => String(path || "").split("/").pop();
const extensionOk = (file) => {
  const ext = String((file && file.name) || "")
    .split(".")
    .pop()
    .toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
};

// ---- Group 1 — Booking page --------------------------------------------------------------------------------------------

const ImageField = () => {
  const { state, update } = useBuilder();
  const d = state.details;
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const file = d.event_image instanceof File ? d.event_image : null;

  // object URL for a freshly chosen file; revoked when it changes / unmounts
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);
  const thumb = previewUrl || (d.event_image_url ? `${IMAGE_BASE_URL}${d.event_image_url}` : "");
  const label = file ? file.name : d.event_image_url ? baseName(d.event_image_url) : "No image selected";

  const onFile = (e) => {
    const chosen = e.target.files && e.target.files[0];
    if (!chosen) return;
    if (!extensionOk(chosen)) {
      // the server silently ignores other extensions (config('constants.image_extensions'))
      setError("Use a JPG, PNG, GIF, BMP or TIFF image.");
      e.target.value = "";
      return;
    }
    setError(null);
    update(STEPS.DETAILS, { event_image: chosen });
  };

  const remove = () => {
    // only a newly chosen file can be dropped — the endpoint has no "clear image" today
    update(STEPS.DETAILS, { event_image: null });
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    // htmlFor → the hidden file input: clicking the text opens the picker; clicks on the inner
    // buttons are not forwarded to the labelled control (they are interactive content themselves)
    <label id="event-image-setting" htmlFor="eb-event-image-file">
      Event image
      <div className={`eb-upload-field ${error ? "eb-has-error" : ""}`.trim()}>
        <span>
          {thumb ? <img src={thumb} alt="" width={40} height={40} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }} /> : null}
          <span>{label}</span>
          {file ? (
            <button type="button" className="eb-text-danger text-danger" onClick={remove} style={{ border: 0, height: "auto", padding: 0, background: "none", color: "rgb(163, 76, 76)" }}>
              Remove
            </button>
          ) : null}
        </span>
        <button type="button" onClick={() => inputRef.current && inputRef.current.click()}>
          Browse
        </button>
      </div>
      <input id="eb-event-image-file" ref={inputRef} type="file" accept={IMAGE_ACCEPT} onChange={onFile} style={{ display: "none" }} aria-label="Event image file" />
      <FieldError message={error} />
    </label>
  );
};

const BookingPageGroup = () => {
  const { state, update, issues, businessId } = useBuilder();
  const t = state.type;
  const published = state.meta.isDraft !== E;
  const serverUrlError = state.save.fieldErrors[STEPS.TYPE].event_url;
  const urlError = serverUrlError || (touched(state, STEPS.TYPE) ? issueFor(issues, "event_url") : null);

  return (
    <SettingsGroup id="booking-page-setting" title="Booking page" text="Details invitees see before selecting a time.">
      <div className="eb-form-settings-grid">
        <label id="event-url-setting">
          Event URL
          <div className={`eb-url-field ${urlError ? "eb-has-error" : ""}`.trim()}>
            <span>{`${FRONTEND_BASE_URL}booking/${businessId}/`}</span>
            <input
              type="text"
              value={t.event_url}
              onChange={(e) => update(STEPS.TYPE, { event_url: e.target.value, urlTouched: true })}
              aria-label="Event URL"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <FieldError message={urlError} />
          {published && !urlError ? <Helper>Changing the URL breaks links you have already shared.</Helper> : null}
        </label>
        <ImageField />
      </div>

      <label className="eb-wide-field" htmlFor="eb-event-description">
        Description / instructions
        <RichOrPlainText
          id="eb-event-description"
          value={state.details.event_description}
          placeholder="Tell invitees what to expect and how to prepare."
          onChange={(v) => update(STEPS.DETAILS, { event_description: v })}
        />
      </label>

      <SettingLine compact title="Make event public" text="List it on your public profile.">
        <Switch
          on={state.advanced.event_public_visiblity_status === E}
          label="Public event"
          onChange={() => update(STEPS.ADVANCED, { event_public_visiblity_status: flip(state.advanced.event_public_visiblity_status) })}
        />
      </SettingLine>
    </SettingsGroup>
  );
};

// ---- Group 2 — Invitee questions & guests -------------------------------------------------------------------------------

const newQuestion = () => ({ question: "", question_type: "text", show_status: E, mandatory_status: D, details: [] });

const QuestionEditor = ({ q, index, issues, onChange, onRemove, onDone }) => {
  const hasOptions = QUESTION_TYPES_WITH_OPTIONS.includes(q.question_type);
  const textError = issueFor(issues, `question:${index}`);
  const optionsError = issueFor(issues, `question:${index}:options`);

  const setType = (type) => {
    // today's onChange: switching to a type without options clears them
    const patch = { question_type: type };
    if (!QUESTION_TYPES_WITH_OPTIONS.includes(type)) patch.details = [];
    onChange(patch);
  };
  const setOption = (i, label) =>
    onChange({
      details: (q.details || []).map((o, k) => (k === i ? { field_label: label, field_value: optionValueFromLabel(label) } : o)),
    });
  const addOption = () => onChange({ details: [...(q.details || []), { field_label: "", field_value: "" }] });
  const removeOption = (i) => onChange({ details: (q.details || []).filter((_, k) => k !== i) });

  return (
    <div className="eb-new-question editing">
      <div className="eb-q-head">
        <input
          type="text"
          value={q.question}
          placeholder="Type your question"
          onChange={(e) => onChange({ question: e.target.value })}
          aria-label="Question"
          autoFocus
          className={textError ? "eb-has-error" : ""}
        />
        <select value={q.question_type} onChange={(e) => setType(e.target.value)} aria-label="Answer type">
          {QUESTION_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <FieldError message={textError} />
      {hasOptions ? (
        <div className="eb-q-options">
          {(q.details || []).map((o, i) => (
            <div key={i}>
              <input type="text" value={o.field_label} placeholder={`Option ${i + 1}`} onChange={(e) => setOption(i, e.target.value)} aria-label={`Option ${i + 1}`} />
              <button type="button" className="eb-row-icon" onClick={() => removeOption(i)} aria-label={`Remove option ${i + 1}`}>
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
          <div>
            <button type="button" className="eb-link-btn" onClick={addOption}>
              + Add option
            </button>
          </div>
          <FieldError message={optionsError} />
        </div>
      ) : null}
      <div className="eb-q-foot">
        <label>
          <input type="checkbox" checked={q.mandatory_status === E} onChange={(e) => onChange({ mandatory_status: e.target.checked ? E : D })} /> Required
        </label>
        <button type="button" className="eb-link-btn" onClick={onDone}>
          Done
        </button>
        <button type="button" className="eb-text-danger text-danger" onClick={onRemove} style={{ marginLeft: "auto" }}>
          Remove
        </button>
      </div>
    </div>
  );
};

const QuestionsGroup = () => {
  const { state, update, issues, hasFeature } = useBuilder();
  const questions = state.advanced.questions || [];
  const [editing, setEditing] = useState(null);
  const guestsAllowed = hasFeature(FEATURE_IDS.GUESTS);
  const guestsOn = state.advanced.invitee_guests_status === E;

  const setQuestions = (next) => update(STEPS.ADVANCED, { questions: next });
  const patchQuestion = (i, patch) => setQuestions(questions.map((q, k) => (k === i ? { ...q, ...patch } : q)));
  const removeQuestion = (i) => {
    setQuestions(questions.filter((_, k) => k !== i));
    setEditing(null);
  };
  const add = () => {
    setQuestions([...questions, newQuestion()]);
    setEditing(questions.length);
  };

  return (
    <SettingsGroup
      id="questions-setting"
      title="Invitee questions & guests"
      text="Collect the information needed to prepare."
      action={
        <button type="button" className="eb-outline-small" onClick={add}>
          + Add new question
        </button>
      }
    >
      <div className="eb-question-row">
        <span className="drag">{"\u22EE\u22EE"}</span>
        <div>
          <strong>Name</strong>
          <small>Short answer</small>
        </div>
        <span className="eb-required-badge">Required</span>
      </div>
      <div className="eb-question-row">
        <span className="drag">{"\u22EE\u22EE"}</span>
        <div>
          <strong>Email address</strong>
          <small>Email</small>
        </div>
        <span className="eb-required-badge">Required</span>
      </div>

      {questions.map((q, i) =>
        editing === i ? (
          <QuestionEditor
            key={`edit-${i}`}
            q={q}
            index={i}
            issues={issues}
            onChange={(patch) => patchQuestion(i, patch)}
            onRemove={() => removeQuestion(i)}
            onDone={() => setEditing(null)}
          />
        ) : (
          <div className="eb-question-row" key={i}>
            <span className="drag">{"\u22EE\u22EE"}</span>
            <button type="button" className="eb-q-title" onClick={() => setEditing(i)} title="Edit question">
              <strong style={issueFor(issues, `question:${i}`) || issueFor(issues, `question:${i}:options`) ? { color: "rgb(163, 76, 76)" } : undefined}>
                {q.question || "Untitled question"}
              </strong>
              <small>
                {questionTypeLabel(q.question_type)}
                {q.mandatory_status === E ? " \u00B7 Required" : ""}
              </small>
            </button>
            <span className="eb-q-actions">
              <Switch on={q.show_status === E} label={q.question || "Question"} onChange={() => patchQuestion(i, { show_status: flip(q.show_status) })} />
            </span>
          </div>
        )
      )}

      <SettingLine compact title="Allow attendee to add an additional guest" text="Collect one extra guest's name and email.">
        {!guestsAllowed ? <PlanBadge kind="upgrade" /> : null}
        <Switch
          on={guestsOn}
          disabled={!guestsAllowed}
          label="Additional guest"
          onChange={() => update(STEPS.ADVANCED, { invitee_guests_status: flip(state.advanced.invitee_guests_status) })}
        />
      </SettingLine>
      {!guestsAllowed && guestsOn ? <DowngradeNote>Guests are not in your plan &mdash; turned off the next time this section is saved.</DowngradeNote> : null}
    </SettingsGroup>
  );
};

// ---- Group 3 — Booking confirmation ---------------------------------------------------------------------------------

const ConfirmationGroup = () => {
  const { state, update, issues, hasFeature, redux } = useBuilder();
  const adv = state.advanced;
  const externalAllowed = hasFeature(FEATURE_IDS.REDIRECT_CUSTOM_PAGES);
  const isExternal = adv.confirmation_page === CONFIRMATION_PAGE.EXTERNAL;
  const shownExternal = isExternal && externalAllowed;
  const urlError = touched(state, STEPS.ADVANCED) ? issueFor(issues, "confirmation_page_url") : null;

  // Require approval gates (plan 6.F): feature 23, org setting, plan cap (unless already on at load)
  const confirmFeature = hasFeature(FEATURE_IDS.STAFF_CONFIRMATION);
  const orgOn = state.lookups.orgStaffCanConfirm === E;
  const capData = (redux.limits || {}).staff_confirm;
  const atCap = Boolean(capData && isAtPlanLimit(capData.limit, capData.used)) && !state.lookups.hadStaffConfirm;
  const approvalOn = adv.staff_can_confirm_booking === E;
  const approvalDisabled = !confirmFeature || !orgOn || (atCap && !approvalOn);
  const capError = issueFor(issues, "staff_can_confirm_booking");

  return (
    <SettingsGroup id="confirmation-setting" title="Booking confirmation" text="Choose what happens after an invitee submits a booking.">
      <div className="eb-radio-panels" role="radiogroup" aria-label="Booking confirmation">
        <label className={!shownExternal ? "selected" : ""}>
          <input type="radio" name="eb-confirmation" checked={!shownExternal} onChange={() => update(STEPS.ADVANCED, { confirmation_page: CONFIRMATION_PAGE.DEFAULT })} />
          <span>
            <strong>MeetOcto confirmation page</strong>
            <small>Show booking details and next steps.</small>
          </span>
        </label>
        <label className={`${shownExternal ? "selected" : ""} ${!externalAllowed ? "muted-option" : ""}`.trim()}>
          <input
            type="radio"
            name="eb-confirmation"
            checked={shownExternal}
            disabled={!externalAllowed}
            onChange={() => externalAllowed && update(STEPS.ADVANCED, { confirmation_page: CONFIRMATION_PAGE.EXTERNAL })}
          />
          <span>
            <strong>
              External page {!externalAllowed ? <PlanBadge kind="upgrade" /> : null}
            </strong>
            <small>Redirect to your own website.</small>
          </span>
        </label>
      </div>
      {isExternal && !externalAllowed ? <DowngradeNote>Redirect pages are not in your plan &mdash; the MeetOcto confirmation page is used the next time this section is saved.</DowngradeNote> : null}

      {shownExternal ? (
        <label className="eb-wide-field" id="confirmation-url-setting">
          Redirect URL
          <input
            type="url"
            value={adv.confirmation_page_url}
            placeholder="https://"
            onChange={(e) => update(STEPS.ADVANCED, { confirmation_page_url: e.target.value })}
            className={urlError ? "eb-has-error" : ""}
          />
          <FieldError message={urlError} />
        </label>
      ) : null}

      <SettingLine compact title="Require approval before confirmation" text="Review requests before a booking is accepted.">
        {!confirmFeature ? <PlanBadge kind="teams" /> : null}
        <Switch
          on={approvalOn}
          disabled={approvalDisabled}
          label="Require approval"
          onChange={() => update(STEPS.ADVANCED, { staff_can_confirm_booking: flip(adv.staff_can_confirm_booking) })}
        />
      </SettingLine>
      {confirmFeature && !orgOn ? (
        <Helper>
          Enable staff confirmation in <Link to={ROUTES.EVENT_SETTINGS}>Event Settings</Link> first.
        </Helper>
      ) : null}
      {confirmFeature && orgOn && atCap && !approvalOn ? <Helper>Plan limit reached for staff confirmation events.</Helper> : null}
      {approvalOn && !confirmFeature ? <DowngradeNote>Staff confirmation is not in your plan &mdash; turned off the next time this section is saved.</DowngradeNote> : null}
      <FieldError message={capError} />
    </SettingsGroup>
  );
};

// ---- Group 4 — Policies & instructions ----------------------------------------------------------------------------------

export const PolicyFields = ({ cancellationPlaceholder = "Add your cancellation policy.", instructionsPlaceholder = "Add arrival, access, or preparation instructions." }) => {
  const { state, update } = useBuilder();
  const d = state.details;
  const set = (patch) => update(STEPS.DETAILS, patch);
  const box = (field, label) => (
    <label key={field}>
      <input type="checkbox" checked={d[field] === E} onChange={(e) => set({ [field]: e.target.checked ? E : D })} /> {label}
    </label>
  );
  return (
    <>
      <div className="eb-policy-grid">
        <label className="eb-wide-field">
          Cancellation policy
          <textarea value={d.cancellation_policy || ""} placeholder={cancellationPlaceholder} onChange={(e) => set({ cancellation_policy: e.target.value })} />
        </label>
        <label className="eb-wide-field">
          Instructions to attendee
          <textarea value={d.instruction_to_attendee || ""} placeholder={instructionsPlaceholder} onChange={(e) => set({ instruction_to_attendee: e.target.value })} />
        </label>
      </div>
      <div className="eb-policy-display">
        <span>Show on</span>
        {box("ita_booking_page_status", "Booking page")}
        {box("ita_confirmation_page_status", "Confirmation page")}
        {box("ita_reminder_email_status", "Reminder email")}
      </div>
    </>
  );
};

const PoliciesGroup = () => {
  const { state, update } = useBuilder();
  return (
    <SettingsGroup id="policies-setting" title="Policies & instructions" text="Set expectations and choose where this information appears.">
      <PolicyFields />
      <SettingLine
        compact
        title="Collect customer feedback"
        text={
          <>
            Ask invitees about their experience. <span className="eb-coming-badge">Coming soon</span>
          </>
        }
      >
        <Switch
          on={state.advanced.customer_feedback_status === E}
          label="Collect feedback"
          onChange={() => update(STEPS.ADVANCED, { customer_feedback_status: flip(state.advanced.customer_feedback_status) })}
        />
      </SettingLine>
    </SettingsGroup>
  );
};

// ---- Section ------------------------------------------------------------------------------------------------------------

const InviteeSection = () => (
  <SettingsView icon="message" title="Invitee experience" text="Shape the booking page, questions, confirmation, and policies.">
    <BookingPageGroup />
    <QuestionsGroup />
    <ConfirmationGroup />
    <PoliciesGroup />
  </SettingsView>
);

export default InviteeSection;
